// Achica las fotos de las respuestas de un cuestionario antes de subirlas.
//
// El envío de un cuestionario sube todas las fotos en un solo request, y el servidor lo corta
// a los 60 s (`config/octane.php` del API). Una foto de celular pesa 4 a 8 MB: el 21/09/2026,
// 24 fotos (67 MB) tardaron 51 s en `combustibles-ar`. Con el lado mayor en 1600 px y JPEG
// quedan en 300 a 600 KB, que alcanza para verlas y para que la IA las valide.
//
// Si algo falla (un formato que el navegador no sabe leer, como HEIC en Chrome), se sube la
// foto original: achicarla nunca puede frenar el envío.
const LADO_MAXIMO = 1600
const CALIDAD = 0.8
// Por debajo de esto no vale la pena recomprimir: se perdería calidad para ganar poco.
const PESO_MINIMO = 500 * 1024
const TIPOS = ['image/jpeg', 'image/png', 'image/webp']

export const comprimirFoto = async (file) => {
    if (!(file instanceof File) || !TIPOS.includes(file.type)) return file

    try {
        // `from-image` respeta la orientación EXIF: al recomprimir, la foto queda derecha.
        const imagen = await createImageBitmap(file, { imageOrientation: 'from-image' })
        const escala = Math.min(1, LADO_MAXIMO / Math.max(imagen.width, imagen.height))
        if (escala === 1 && file.size <= PESO_MINIMO) {
            imagen.close()
            return file
        }

        const canvas = document.createElement('canvas')
        canvas.width = Math.round(imagen.width * escala)
        canvas.height = Math.round(imagen.height * escala)
        const ctx = canvas.getContext('2d')
        // Fondo blanco: un PNG con transparencia pasado a JPEG queda negro donde no había nada.
        ctx.fillStyle = '#fff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(imagen, 0, 0, canvas.width, canvas.height)
        imagen.close()

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', CALIDAD))
        if (!blob || blob.size >= file.size) return file

        const nombre = file.name.replace(/\.[^.]*$/, '') + '.jpg'
        return new File([blob], nombre, { type: 'image/jpeg', lastModified: file.lastModified })
    } catch (error) {
        return file
    }
}

// Devuelve otro FormData con las mismas claves, en el mismo orden, y las fotos achicadas.
// ponytail: de a una foto por vez; en paralelo, 26 fotos de 12 MP decodificadas juntas pueden
// ocupar más de 1 GB y cerrar la pestaña en un celular. Si tarda, probar de a dos.
export const comprimirFotosDe = async (formData) => {
    if (!(formData instanceof FormData)) return formData

    const salida = new FormData()
    for (const [clave, valor] of formData.entries()) {
        if (valor instanceof File) {
            const foto = await comprimirFoto(valor)
            salida.append(clave, foto, foto.name)
        } else {
            salida.append(clave, valor)
        }
    }
    return salida
}
